/**
 * API Endpoints Mapping
 * Type-safe endpoint definitions matching NestJS backend structure
 */

/**
 * Authentication endpoints
 */
export const authEndpoints = {
  login: '/auth/login',
  logout: '/auth/logout',
  register: '/auth/register',
  refresh: '/auth/refresh',
  verify: '/auth/verify',
  resendVerification: '/auth/resend-verification',
  forgotPassword: '/auth/forgot-password',
  resetPassword: '/auth/reset-password',
  profile: '/auth/profile',
  updateProfile: '/auth/profile',
  changePassword: '/auth/change-password',
  requestMagicLink: '/auth/magic-login',
  magicLinkCallback: '/auth/magic-login/callback',
} as const;

/**
 * User endpoints
 */
export const userEndpoints = {
  list: '/users',
  get: (id: string) => `/users/${id}`,
  create: '/users',
  update: (id: string) => `/users/${id}`,
  delete: (id: string) => `/users/${id}`,
  search: '/users/search',
  getAllRoles: '/users/roles',
  updateRole: (id: string) => `/users/${id}/role`,
} as const;

/**
 * Store/Storefront endpoints
 */
export const storeEndpoints = {
  list: '/stores',
  get: (id: string) => `/stores/${id}`,
  create: '/stores',
  update: (id: string) => `/stores/${id}`,
  delete: (id: string) => `/stores/${id}`,
  search: '/stores/search',
  browse: '/stores/browse',
  getBySlug: (slug: string) => `/stores/slug/${slug}`,
  getAnalytics: (id: string) => `/stores/${id}/analytics`,
  getOfferings: (id: string) => `/stores/${id}/offerings`,
} as const;

/**
 * Product/Offering endpoints
 */
export const offeringEndpoints = {
  list: '/offerings',
  get: (id: string) => `/offerings/${id}`,
  create: '/offerings',
  update: (id: string) => `/offerings/${id}`,
  delete: (id: string) => `/offerings/${id}`,
  search: '/offerings/search',
  getByStore: (storeId: string) => `/stores/${storeId}/offerings`,
  createByStore: (storeId: string) => `/stores/${storeId}/offerings`,
  getRecommended: '/offerings/recommended',
  getFeatured: '/offerings/featured',
} as const;

/**
 * Order endpoints
 */
export const orderEndpoints = {
  list: '/orders',
  get: (id: string) => `/orders/${id}`,
  create: '/orders',
  update: (id: string) => `/orders/${id}`,
  cancel: (id: string) => `/orders/${id}/cancel`,
  getByUser: '/orders/user',
  getByStore: (storeId: string) => `/stores/${storeId}/orders`,
  getStatus: (id: string) => `/orders/${id}/status`,
  updateStatus: (id: string) => `/orders/${id}/status`,
  trackShipment: (id: string) => `/orders/${id}/tracking`,
} as const;

/**
 * Payment endpoints
 */
export const paymentEndpoints = {
  create: '/payments',
  get: (id: string) => `/payments/${id}`,
  process: '/payments/process',
  confirm: (id: string) => `/payments/${id}/confirm`,
  cancel: (id: string) => `/payments/${id}/cancel`,
  refund: (id: string) => `/payments/${id}/refund`,
  getByOrder: (orderId: string) => `/orders/${orderId}/payments`,
  getPaymentMethods: '/payments/methods',
  savePaymentMethod: '/payments/methods',
  deletePaymentMethod: (methodId: string) => `/payments/methods/${methodId}`,
  webhook: '/payments/webhook',
} as const;

/**
 * Subscription endpoints
 */
export const subscriptionEndpoints = {
  list: '/subscriptions',
  get: (id: string) => `/subscriptions/${id}`,
  create: '/subscriptions',
  update: (id: string) => `/subscriptions/${id}`,
  cancel: (id: string) => `/subscriptions/${id}/cancel`,
  pause: (id: string) => `/subscriptions/${id}/pause`,
  resume: (id: string) => `/subscriptions/${id}/resume`,
  getByUser: '/subscriptions/user',
  getByStore: (storeId: string) => `/stores/${storeId}/subscriptions`,
  getPlan: (planId: string) => `/subscriptions/plans/${planId}`,
  listPlans: '/subscriptions/plans',
} as const;

/**
 * Coupon/Discount endpoints
 */
export const couponEndpoints = {
  list: '/coupons',
  get: (id: string) => `/coupons/${id}`,
  create: '/coupons',
  update: (id: string) => `/coupons/${id}`,
  delete: (id: string) => `/coupons/${id}`,
  validate: '/coupons/validate',
  getByCode: (code: string) => `/coupons/code/${code}`,
  getByStore: (storeId: string) => `/stores/${storeId}/coupons`,
} as const;

/**
 * Analytics endpoints
 */
export const analyticsEndpoints = {
  getOverview: '/analytics/overview',
  getSalesData: (storeId?: string) => `/analytics/sales${storeId ? `?storeId=${storeId}` : ''}`,
  getRevenueData: (storeId?: string) => `/analytics/revenue${storeId ? `?storeId=${storeId}` : ''}`,
  getCustomerMetrics: (storeId?: string) =>
    `/analytics/customers${storeId ? `?storeId=${storeId}` : ''}`,
  getProductMetrics: (storeId?: string) =>
    `/analytics/products${storeId ? `?storeId=${storeId}` : ''}`,
  getPerformanceMetrics: (storeId?: string) =>
    `/analytics/performance${storeId ? `?storeId=${storeId}` : ''}`,
} as const;

/**
 * Review/Rating endpoints
 */
export const reviewEndpoints = {
  list: '/reviews',
  get: (id: string) => `/reviews/${id}`,
  create: '/reviews',
  update: (id: string) => `/reviews/${id}`,
  delete: (id: string) => `/reviews/${id}`,
  getByOffering: (offeringId: string) => `/offerings/${offeringId}/reviews`,
  getByStore: (storeId: string) => `/stores/${storeId}/reviews`,
  getByUser: '/reviews/user',
  updateRating: (id: string) => `/reviews/${id}/rating`,
} as const;

/**
 * Upload endpoints
 */
export const uploadEndpoints = {
  uploadImage: '/upload/image',
  uploadFile: '/upload/file',
  uploadMultiple: '/upload/multiple',
  deleteFile: (fileId: string) => `/upload/${fileId}`,
  getFile: (fileId: string) => `/upload/${fileId}`,
} as const;

/**
 * Notification endpoints
 */
export const notificationEndpoints = {
  list: '/notifications',
  unreadCount: '/notifications/unread-count',
  markRead: (id: string) => `/notifications/${id}/read`,
  markAllRead: '/notifications/read-all',
  savePushSubscription: '/notifications/push-subscription',
  removePushSubscription: '/notifications/push-subscription',
} as const;

/**
 * Health/Status endpoints
 */
export const healthEndpoints = {
  status: '/health',
  version: '/version',
  dependencies: '/health/dependencies',
} as const;

/**
 * Dispute endpoints
 */
export const disputeEndpoints = {
  list: '/disputes',
  get: (id: string) => `/disputes/${id}`,
  resolve: (id: string) => `/disputes/${id}/resolve`,
} as const;

/**
 * Report endpoints
 */
export const reportEndpoints = {
  list: '/reports',
  get: (id: string) => `/reports/${id}`,
  review: (id: string) => `/reports/${id}/review`,
} as const;

/**
 * Admin endpoints
 */
export const adminEndpoints = {
  users: '/admin/users',
  stats: '/admin/stats',
  userCredibility: (id: string) => `/admin/users/${id}/credibility`,
  updateUserStatus: (id: string) => `/admin/users/${id}/status`,
  payments: '/admin/payments',
  paymentStats: '/admin/payments/stats',
  subaccounts: '/admin/subaccounts',
} as const;

/**
 * Combined endpoints object
 */
export const endpoints = {
  auth: authEndpoints,
  users: userEndpoints,
  stores: storeEndpoints,
  offerings: offeringEndpoints,

  orders: orderEndpoints,
  payments: paymentEndpoints,
  subscriptions: subscriptionEndpoints,
  coupons: couponEndpoints,
  analytics: analyticsEndpoints,
  reviews: reviewEndpoints,
  upload: uploadEndpoints,
  health: healthEndpoints,
  notifications: notificationEndpoints,
  disputes: disputeEndpoints,
  reports: reportEndpoints,
  admin: adminEndpoints,
} as const;

/**
 * Get full endpoint URL
 * Useful for building complete URLs with base domain
 */
export function getEndpointUrl(baseUrl: string, endpoint: string): string {
  const cleanBase = baseUrl.replace(/\/$/, '');
  const cleanEndpoint = endpoint.replace(/^\//, '');
  return `${cleanBase}/${cleanEndpoint}`;
}
